/**
 * Bonsai Pen document creator: one picker and one form for every playbook
 * the backend offers (/api/bonsai-pen/docs/types). The form is built from
 * each playbook's field list, so a new document type needs no frontend work.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { LOGO_URL } from "../config/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  ArrowLeft, Building2, CheckCircle2, Download, FileText, FolderUp, Loader2, Lock, PenLine,
  Search, Shield, AlertTriangle, XCircle, Users, Handshake, Scale, Sparkles,
} from "lucide-react";

const ICONS = { nda: Lock, services: Handshake, dpa: Shield, ip_assignment: Sparkles, resolutions: Users,
  sha: Building2, tcs: Scale };

const PARTY_KINDS = [
  { value: "company", label: "Company" }, { value: "llp", label: "LLP" }, { value: "individual", label: "Individual" },
];

export function isShown(field, answers) {
  if (!field.show_if) return true;
  return Object.entries(field.show_if).every(([k, want]) => {
    const have = answers[k];
    return Array.isArray(want) ? want.includes(have) : have === want;
  });
}

function downloadBase64(b64, filename) {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  const blob = new Blob([bytes], {
    type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function PartyInput({ field, value, onChange }) {
  const v = value || { kind: "company" };
  const [looking, setLooking] = useState(false);
  const set = (k, x) => onChange({ ...v, [k]: x });
  const lookUp = async () => {
    if (!v.number) { toast.error("Type the company number first."); return; }
    setLooking(true);
    try {
      const r = await axios.get(`${API}/late-payment-companies-house`, {
        params: { number: v.number }, withCredentials: true,
      });
      const c = r.data?.company || {};
      onChange({ ...v, name: c.name || v.name, number: c.company_number || v.number,
        address: (c.registered_office || "").replace(/\n/g, ", ") || v.address });
      toast.success(`Filled from Companies House (${c.status || "status unknown"}).`);
    } catch (e) {
      toast.error(errorMessage(e, "Companies House lookup failed."));
    }
    setLooking(false);
  };
  return (
    <div className="space-y-2 rounded-lg border border-navy-border p-3" data-testid={`party-${field.key}`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <Select value={v.kind || "company"} onValueChange={(x) => set("kind", x)}>
          <SelectTrigger className="bg-navy border-navy-border text-white text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PARTY_KINDS.map((k) => <SelectItem key={k.value} value={k.value}>{k.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input value={v.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Full legal name"
          className="md:col-span-2 bg-navy border-navy-border text-white text-sm" data-testid={`party-${field.key}-name`} />
      </div>
      {v.kind !== "individual" && (
        <div className="flex gap-2">
          <Input value={v.number || ""} onChange={(e) => set("number", e.target.value)} placeholder="Company number"
            className="bg-navy border-navy-border text-white text-sm" />
          <Button type="button" size="sm" variant="outline" onClick={lookUp} disabled={looking}
            className="gap-1.5 text-xs border-navy-border text-slate-300">
            {looking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
            Companies House
          </Button>
        </div>
      )}
      <Input value={v.address || ""} onChange={(e) => set("address", e.target.value)}
        placeholder={v.kind === "individual" ? "Home address" : "Registered office"}
        className="bg-navy border-navy-border text-white text-sm" />
      {v.kind !== "individual" && (
        <Input value={v.signatory || ""} onChange={(e) => set("signatory", e.target.value)}
          placeholder={v.kind === "llp" ? "Member signing (optional)" : "Director signing (optional)"}
          className="bg-navy border-navy-border text-white text-sm" />
      )}
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const cls = "bg-navy border-navy-border text-white text-sm";
  if (field.kind === "party") return <PartyInput field={field} value={value} onChange={onChange} />;
  if (field.kind === "bool") {
    return (
      <div className="flex items-center gap-3">
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={`field-${field.key}`} />
        <span className="text-sm text-slate-300">{field.label}</span>
      </div>
    );
  }
  if (field.kind === "select") {
    return (
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger className={cls} data-testid={`field-${field.key}`}><SelectValue placeholder="Choose" /></SelectTrigger>
        <SelectContent>
          {field.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (field.kind === "textarea") {
    return <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className={cls}
      data-testid={`field-${field.key}`} />;
  }
  return (
    <Input type={field.kind === "date" ? "date" : "text"} value={value || ""} onChange={(e) => onChange(e.target.value)}
      placeholder={field.kind === "money" ? "£" : ""} className={cls} data-testid={`field-${field.key}`} />
  );
}

function MatterPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef(null);
  const search = useCallback(async (text) => {
    if (text.length < 2) { setResults([]); return; }
    setBusy(true);
    try {
      const r = await axios.get(`${API}/bonsai/filing/matters`, { params: { q: text, limit: 6 }, withCredentials: true });
      setResults(r.data || []);
    } catch { setResults([]); }
    setBusy(false);
  }, []);
  if (value) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded border border-teal/40 bg-teal-dim/10">
        <span className="text-sm text-teal flex-1" data-testid="matter-picked">{value}</span>
        <button type="button" onClick={() => onChange("")} className="text-slate-500 hover:text-slate-300" aria-label="Clear matter">
          <XCircle className="w-3.5 h-3.5" />
        </button>
      </div>
    );
  }
  return (
    <div className="relative">
      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
      <Input value={q} placeholder="Search matters..." className="pl-8 bg-navy border-navy-border text-white text-sm"
        data-testid="docs-matter-search"
        onChange={(e) => {
          setQ(e.target.value);
          if (timer.current) clearTimeout(timer.current);
          const text = e.target.value;
          timer.current = setTimeout(() => search(text), 300);
        }} />
      {busy && <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-teal animate-spin" />}
      {results.length > 0 && (
        <div className="absolute z-20 top-full mt-1 left-0 right-0 border border-navy-border rounded bg-navy-card max-h-48 overflow-y-auto shadow-xl">
          {results.map((m, i) => (
            <div key={i} onClick={() => { onChange(m.reference || m.matter_reference); setResults([]); setQ(""); }}
              className="px-3 py-2 hover:bg-teal-dim/30 cursor-pointer border-b border-navy-border/50 last:border-0">
              <div className="text-xs text-white font-medium">{m.matter_title || m.reference}</div>
              <div className="text-[10px] text-slate-500">{m.reference}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Picker({ types, also, onPick, navigate }) {
  const groups = useMemo(() => {
    const g = {};
    [...types.map((t) => ({ ...t, live: true })), ...also.map((t) => ({ ...t, external: true }))].forEach((t) => {
      (g[t.category] = g[t.category] || []).push(t);
    });
    return g;
  }, [types, also]);
  return (
    <div className="space-y-8" data-testid="docs-picker">
      {Object.entries(groups).map(([cat, items]) => (
        <div key={cat}>
          <h2 className="text-xs font-medium text-teal uppercase tracking-wider mb-3">{cat}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((t) => {
              const Icon = ICONS[t.id] || FileText;
              return (
                <Card key={t.id} data-testid={`doc-card-${t.id}`}
                  className="cursor-pointer border-2 border-navy-border bg-navy hover:border-teal transition-all"
                  onClick={() => (t.external ? navigate(t.route) : onPick(t.id))}>
                  <CardContent className="p-5">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-xl bg-teal-dim/40 flex items-center justify-center">
                        <Icon className="w-5 h-5 text-teal" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="text-sm font-semibold text-white">{t.name}</h3>
                          {(t.coco_refs || []).map((r) => (
                            <Badge key={r} className="bg-navy-hover text-slate-400 text-[9px]">{r}</Badge>
                          ))}
                        </div>
                        <p className="text-xs text-slate-400">{t.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function Result({ result }) {
  const { checker, not_covered: notCovered, filed, model_ran: modelRan } = result;
  return (
    <div className="rounded-lg border border-navy-border bg-navy p-4 space-y-3" data-testid="docs-result">
      <div className="flex items-center gap-2 flex-wrap">
        <CheckCircle2 className="w-4 h-4 text-status-green" />
        <span className="text-sm text-white">{result.filename}</span>
        <Button size="sm" variant="outline" className="gap-1.5 text-xs border-navy-border text-slate-300 ml-auto"
          onClick={() => downloadBase64(result.docx_base64, result.filename)}>
          <Download className="w-3.5 h-3.5" /> Download again
        </Button>
      </div>
      {filed && filed.status === "filed" && (
        <p className="text-xs text-slate-300">
          Filed to the matter&apos;s Drafts folder.{" "}
          {filed.web_url && <a href={filed.web_url} target="_blank" rel="noreferrer" className="text-teal underline">Open in SharePoint</a>}
        </p>
      )}
      {filed && filed.status !== "filed" && (
        <p className="text-xs text-status-amber" data-testid="docs-filed-failed">
          Not filed to SharePoint: {filed.error}. The download above is the same draft.
        </p>
      )}
      {!modelRan && (
        <p className="text-xs text-status-amber">The AI step did not run, so the deal wording uses your answers or bracketed gaps.</p>
      )}
      {checker.errors.length > 0 && (
        <div className="text-xs text-status-red">
          <div className="flex items-center gap-1.5 font-medium"><AlertTriangle className="w-3.5 h-3.5" /> The automatic check found problems</div>
          <ul className="list-disc ml-5">{checker.errors.map((e, i) => <li key={i}>{e.message} {e.where}</li>)}</ul>
        </div>
      )}
      {notCovered.length > 0 && (
        <div className="text-xs text-slate-300">
          <div className="font-medium">Your instructions the standard draft does not cover</div>
          <ul className="list-disc ml-5">{notCovered.map((t, i) => <li key={i}>{t}</li>)}</ul>
        </div>
      )}
      <p className="text-xs text-slate-400">
        {checker.gaps.length} bracketed gap{checker.gaps.length === 1 ? "" : "s"} to fill. The full list and the points to
        check are on the drafter&apos;s note, the last page of the draft.
      </p>
    </div>
  );
}

export default function BonsaiPenDocs() {
  const navigate = useNavigate();
  const { type } = useParams();
  const [types, setTypes] = useState([]);
  const [also, setAlso] = useState([]);
  const [loading, setLoading] = useState(true);
  const [answers, setAnswers] = useState({});
  const [matterRef, setMatterRef] = useState("");
  const [fileToDrafts, setFileToDrafts] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/bonsai-pen/docs/types`, { withCredentials: true });
        setTypes(r.data?.types || []);
        setAlso(r.data?.also || []);
      } catch (e) {
        toast.error(errorMessage(e, "Could not load the document types."));
      }
      setLoading(false);
    })();
  }, []);

  const pb = types.find((t) => t.id === type);

  useEffect(() => {
    if (!pb) return;
    const d = {};
    pb.fields.forEach((f) => { if (f.default !== null && f.default !== undefined) d[f.key] = f.default; });
    setAnswers(d);
    setResult(null);
  }, [pb]);

  const sections = useMemo(() => {
    if (!pb) return [];
    const order = [];
    const by = {};
    pb.fields.forEach((f) => {
      const s = f.section || "Details";
      if (!by[s]) { by[s] = []; order.push(s); }
      by[s].push(f);
    });
    return order.map((s) => [s, by[s]]);
  }, [pb]);

  const generate = async () => {
    setBusy(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/bonsai-pen/docs/generate`, {
        type: pb.id, answers, matter_ref: matterRef, file_to_drafts: !!(matterRef && fileToDrafts),
      }, { withCredentials: true });
      setResult(r.data);
      downloadBase64(r.data.docx_base64, r.data.filename);
      toast.success("Draft ready.");
    } catch (e) {
      toast.error(errorMessage(e, "The draft could not be produced."));
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-navy-card" data-testid="bonsai-pen-docs-page">
      <div className="border-b border-navy-border bg-navy">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Logo" className="w-7 h-7 rounded" />
            <span className="text-sm font-semibold text-white">CaseMinder</span>
          </Link>
          <span className="text-slate-600">/</span>
          <Link to="/bonsai-pen" className="flex items-center gap-1.5">
            <PenLine className="w-4 h-4 text-teal" />
            <span className="text-sm font-bold text-teal">Bonsai Pen</span>
          </Link>
          {pb && (<><span className="text-slate-600">/</span><span className="text-sm text-white">{pb.name}</span></>)}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-8">
        {loading && <div className="text-center py-16"><Loader2 className="w-6 h-6 animate-spin text-teal mx-auto" /></div>}

        {!loading && !type && (
          <>
            <div className="mb-8">
              <h1 className="text-2xl font-bold text-white mb-1">First drafts in house style</h1>
              <p className="text-sm text-slate-400 max-w-2xl">
                Pick a document, answer a few questions, and get a Word draft on the firm&apos;s standard terms, with a
                drafter&apos;s note on the last page listing the gaps and points to check. It is a starting point for a
                fee earner, not a finished document.
              </p>
            </div>
            <Picker types={types} also={also} navigate={navigate} onPick={(id) => navigate(`/bonsai-pen/docs/${id}`)} />
          </>
        )}

        {!loading && type && !pb && (
          <p className="text-sm text-slate-400">That document type does not exist. <Link className="text-teal underline" to="/bonsai-pen/docs">Back to the list</Link></p>
        )}

        {pb && (
          <div className="space-y-6" data-testid={`docs-form-${pb.id}`}>
            <button type="button" onClick={() => navigate("/bonsai-pen/docs")} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-teal">
              <ArrowLeft className="w-3.5 h-3.5" /> All documents
            </button>
            <div>
              <h1 className="text-xl font-bold text-white">{pb.name}</h1>
              <p className="text-sm text-slate-400">{pb.description}</p>
            </div>

            {sections.map(([title, fields]) => {
              const visible = fields.filter((f) => isShown(f, answers));
              if (!visible.length) return null;
              return (
                <div key={title} className="space-y-3">
                  <h2 className="text-xs font-medium text-teal uppercase tracking-wider">{title}</h2>
                  {visible.map((f) => (
                    <div key={f.key} className="space-y-1.5">
                      {f.kind !== "bool" && (
                        <Label className="text-xs text-slate-300">{f.label}{f.required && <span className="text-status-red"> *</span>}</Label>
                      )}
                      <FieldInput field={f} value={answers[f.key]} onChange={(v) => setAnswers((a) => ({ ...a, [f.key]: v }))} />
                      {f.help && <p className="text-[11px] text-slate-500">{f.help}</p>}
                    </div>
                  ))}
                </div>
              );
            })}

            <div className="space-y-3 border-t border-navy-border pt-4">
              <h2 className="text-xs font-medium text-teal uppercase tracking-wider">Where the draft goes</h2>
              <MatterPicker value={matterRef} onChange={setMatterRef} />
              {matterRef && (
                <div className="flex items-center gap-3">
                  <Switch checked={fileToDrafts} onCheckedChange={setFileToDrafts} data-testid="docs-file-to-drafts" />
                  <span className="text-sm text-slate-300 flex items-center gap-1.5"><FolderUp className="w-3.5 h-3.5" /> Also file it to the matter&apos;s Drafts folder</span>
                </div>
              )}
              <p className="text-[11px] text-slate-500">The draft always downloads. Without a matter, nothing is filed.</p>
            </div>

            <Button onClick={generate} disabled={busy} className="gap-2" data-testid="docs-generate">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              {busy ? "Drafting..." : "Produce the draft"}
            </Button>

            {result && <Result result={result} />}
          </div>
        )}
      </div>
    </div>
  );
}
